// Cookie-backed storage for the local-auth session (see authBackend.js).
// Chunks values so large access+refresh token JSON fits under typical 4KB cookie limits.
// Falls back to reading legacy localStorage once so existing logins survive the switch.

const MAX_CHUNK = 3180; // leave headroom for cookie attributes
const YEAR = 60 * 60 * 24 * 365;

function cookieAttrs() {
  const secure = typeof window !== "undefined" && window.location?.protocol === "https:" ? "; Secure" : "";
  return `; Path=/; Max-Age=${YEAR}; SameSite=Lax${secure}`;
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}${cookieAttrs()}`;
}

function clearCookie(name) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function chunkNames(key) {
  // key = whole value in one cookie; key.0, key.1, ... = chunks
  const names = [key];
  for (let i = 0; i < 20; i += 1) names.push(`${key}.${i}`);
  return names;
}

function readAllChunks(key) {
  const single = readCookie(key);
  if (single != null) return single;

  const parts = [];
  for (let i = 0; i < 20; i += 1) {
    const part = readCookie(`${key}.${i}`);
    if (part == null) break;
    parts.push(part);
  }
  return parts.length ? parts.join("") : null;
}

function writeChunked(key, value) {
  // Clear previous single + chunks first
  for (const name of chunkNames(key)) clearCookie(name);

  if (value.length <= MAX_CHUNK) {
    writeCookie(key, value);
    return;
  }
  const count = Math.ceil(value.length / MAX_CHUNK);
  for (let i = 0; i < count; i += 1) {
    writeCookie(`${key}.${i}`, value.slice(i * MAX_CHUNK, (i + 1) * MAX_CHUNK));
  }
}

function clearChunked(key) {
  for (const name of chunkNames(key)) clearCookie(name);
}

function migrateFromLocalStorage(key) {
  try {
    if (typeof localStorage === "undefined") return null;
    const legacy = localStorage.getItem(key);
    if (!legacy) return null;
    writeChunked(key, legacy);
    localStorage.removeItem(key);
    return legacy;
  } catch {
    return null;
  }
}

export const authCookieStorage = {
  getItem(key) {
    const fromCookie = readAllChunks(key);
    if (fromCookie != null) return fromCookie;
    return migrateFromLocalStorage(key);
  },
  setItem(key, value) {
    writeChunked(key, String(value ?? ""));
    try {
      if (typeof localStorage !== "undefined") localStorage.removeItem(key);
    } catch {
      /* ignore quota / private mode */
    }
  },
  removeItem(key) {
    clearChunked(key);
    try {
      if (typeof localStorage !== "undefined") localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};
